/**
 * PendingUnlockRequestsStrip — iter101.49
 *
 * Renders on the admin case detail page above the tab nav when the
 * case has pending unlock requests (from the planner). Admin picks
 * a decision (Approve / Deny) and writes a mandatory note; the
 * backend flips `case.plan_unlocked_at` or `case.mfg_unlocked_at`
 * on approval and historises the resolution either way.
 *
 * Fetches from `/api/cases/{id}/unlock-requests` on mount. Fires
 * `onResolved` after every successful resolve so the parent can
 * refresh the case data (the request pill disappears / status
 * reflects the new state).
 */
import React, { useEffect, useState } from "react";
import { LockOpen, PaperPlaneTilt, Warning, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const SECTION_LABELS = { plan: "Clinical plan", mfg_spec: "Manufacturing spec" };

export default function PendingUnlockRequestsStrip({ caseId, onResolved }) {
    const [pending, setPending] = useState([]);
    const [active, setActive] = useState(null);        // req currently being resolved
    const [decision, setDecision] = useState("approved");
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const reload = async () => {
        try {
            const { data } = await api.get(`/cases/${caseId}/unlock-requests`);
            setPending((data.requests || []).filter((r) => r.status === "pending"));
        } catch {
            /* silent */
        }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseId]);

    const open = (req) => {
        setActive(req);
        setDecision("approved");
        setNote("");
    };
    const close = () => {
        if (submitting) return;
        setActive(null);
        setNote("");
    };

    const submit = async () => {
        const trimmed = note.trim();
        if (trimmed.length < 4) {
            toast.error("Add a short admin note (at least 4 characters).");
            return;
        }
        setSubmitting(true);
        try {
            await api.put(
                `/admin/cases/${caseId}/unlock-requests/${active.id}`,
                { decision, note: trimmed },
            );
            toast.success(decision === "approved" ? "Unlock granted" : "Request denied");
            setActive(null);
            setNote("");
            await reload();
            onResolved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to resolve");
        } finally {
            setSubmitting(false);
        }
    };

    if (!pending.length) return null;

    return (
        <div
            data-testid="admin-unlock-requests-strip"
            className="mt-3 rounded-2xl border border-amber-300 bg-amber-50/70 px-4 py-3 space-y-2 shadow-sm"
        >
            <div className="flex items-center gap-2">
                <Warning size={14} weight="fill" className="text-amber-800" />
                <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-amber-900">
                    Pending unlock request{pending.length === 1 ? "" : "s"}
                </div>
                <div className="text-[10.5px] text-amber-800/80">
                    · Planner is waiting on your decision
                </div>
            </div>
            <div className="space-y-1.5">
                {pending.map((r) => (
                    <div
                        key={r.id}
                        data-testid={`admin-unlock-request-${r.id}`}
                        className="flex flex-wrap items-start gap-3 rounded-xl bg-white/80 border border-amber-200 px-3 py-2"
                    >
                        <div className="flex-1 min-w-[220px]">
                            <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                                <span className="font-bold text-amber-900 uppercase tracking-wider text-[10px]">
                                    {SECTION_LABELS[r.section] || r.section}
                                </span>
                                <span className="text-muted-foreground">·</span>
                                <span className="font-semibold text-foreground">{r.requested_by_name}</span>
                                {r.created_at && (
                                    <>
                                        <span className="text-muted-foreground">·</span>
                                        <span className="text-muted-foreground tabular-nums">
                                            {new Date(r.created_at).toLocaleString("en-IN", {
                                                day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                                            })}
                                        </span>
                                    </>
                                )}
                            </div>
                            <p className="text-[12.5px] text-foreground/85 mt-1 leading-snug italic">
                                &ldquo;{r.reason}&rdquo;
                            </p>
                        </div>
                        <Button
                            size="sm"
                            onClick={() => open(r)}
                            data-testid={`admin-unlock-resolve-${r.id}`}
                            className="bg-amber-700 hover:bg-amber-800 text-white h-8 text-[12px]"
                        >
                            <LockOpen size={12} weight="bold" className="mr-1.5" />
                            Resolve
                        </Button>
                    </div>
                ))}
            </div>
            <Dialog open={!!active} onOpenChange={(v) => { if (!v) close(); }}>
                {active && (
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <LockOpen size={16} weight="duotone" className="text-primary" />
                                Resolve unlock request
                            </DialogTitle>
                            <DialogDescription className="text-[12.5px]">
                                <span className="font-semibold text-foreground">
                                    {SECTION_LABELS[active.section] || active.section}
                                </span>{" "}
                                — requested by{" "}
                                <span className="font-semibold text-foreground">
                                    {active.requested_by_name}
                                </span>
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div className="rounded-lg bg-secondary/30 border border-border px-3 py-2">
                                <div className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-muted-foreground mb-1">
                                    Planner&apos;s reason
                                </div>
                                <p className="text-[12.5px] text-foreground italic leading-relaxed">
                                    &ldquo;{active.reason}&rdquo;
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setDecision("approved")}
                                    className={`flex-1 rounded-xl border px-3 py-2 text-left transition ${
                                        decision === "approved"
                                            ? "bg-emerald-50 border-emerald-400 text-emerald-900"
                                            : "border-border hover:border-primary/40"
                                    }`}
                                    data-testid="admin-unlock-decision-approved"
                                >
                                    <div className="text-[11px] font-bold uppercase tracking-wider">Approve</div>
                                    <div className="text-[10.5px] text-muted-foreground mt-0.5 leading-snug">
                                        Unlocks the block · re-locks after re-submit
                                    </div>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setDecision("denied")}
                                    className={`flex-1 rounded-xl border px-3 py-2 text-left transition ${
                                        decision === "denied"
                                            ? "bg-rose-50 border-rose-400 text-rose-900"
                                            : "border-border hover:border-primary/40"
                                    }`}
                                    data-testid="admin-unlock-decision-denied"
                                >
                                    <div className="text-[11px] font-bold uppercase tracking-wider">Deny</div>
                                    <div className="text-[10.5px] text-muted-foreground mt-0.5 leading-snug">
                                        Keeps the block locked · planner is notified
                                    </div>
                                </button>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                                    Admin note <span className="text-rose-600">*</span>
                                </label>
                                <Textarea
                                    rows={3}
                                    value={note}
                                    onChange={(e) => setNote(e.target.value)}
                                    placeholder={
                                        decision === "approved"
                                            ? "e.g. Approved — please revise the molar attachment position and re-submit."
                                            : "e.g. Denied — this change should be raised as a formal revision request from the doctor."
                                    }
                                    className="text-sm resize-none"
                                    data-testid="admin-unlock-note"
                                />
                            </div>
                        </div>
                        <DialogFooter className="gap-2">
                            <Button
                                variant="outline"
                                onClick={close}
                                disabled={submitting}
                                data-testid="admin-unlock-cancel"
                            >
                                <X size={13} className="mr-1" /> Cancel
                            </Button>
                            <Button
                                onClick={submit}
                                disabled={submitting || note.trim().length < 4}
                                data-testid="admin-unlock-confirm"
                                className={
                                    decision === "approved"
                                        ? "bg-emerald-600 hover:bg-emerald-700"
                                        : "bg-rose-600 hover:bg-rose-700"
                                }
                            >
                                <PaperPlaneTilt size={13} weight="fill" className="mr-1.5" />
                                {submitting ? "Saving…" : decision === "approved" ? "Approve unlock" : "Deny request"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                )}
            </Dialog>
        </div>
    );
}
